import type { ReactNode } from 'react';
import { colorForAgent } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import type { MentionAgent } from '../../../../lib/agent-mentions';
import type { GroupMentionCandidate } from '../../../../lib/group-chat-actions';
import { GROUP_AVATAR } from '../../../../lib/group-chat';
import { GroupParticipantAvatar } from '../../group-chat/GroupAvatars';
import { ScrollArea } from '../../ui';
import styles from './AgentMentionPanel.module.css';

/** A Symphony GROUP chat's `@` panel: two sections, People and Assistants (Phase 3b). */
export interface GroupMentionSections {
  people: GroupMentionCandidate[];
  assistants: GroupMentionCandidate[];
  onPick: (candidate: GroupMentionCandidate) => void;
}

// The @-mention panel floating above the composer: the user's agents (shared
// identity color dot + name), filtered by what was typed after the `@`. Web is
// agents-only — contacts stay on the "+" menu (there is no merged panel here).
// Test tags mirror the native surfaces: chat.mention.panel / chat.mention.agentRow.
//
// In a Symphony GROUP chat (behind `symphonyGroupChat`) it lists the group instead: People (the other humans) and
// Assistants (the Maestros), each with its own avatar — the same two sections iOS and Android draw.
export function AgentMentionPanel({ agents, onPick, group }: { agents: MentionAgent[]; onPick: (agent: MentionAgent) => void; group?: GroupMentionSections }) {
  const { t } = useTranslation();
  if (group) {
    const section = (title: string, rows: GroupMentionCandidate[], testId: string) =>
      rows.length > 0 && (
        <>
          <div className={styles.header}>{title}</div>
          {rows.map((c) => (
            <MentionRow key={c.id} testId={testId} onPick={() => group.onPick(c)}
              mark={<GroupParticipantAvatar participant={c.participant} size={GROUP_AVATAR.compact.size} />} label={c.label} />
          ))}
        </>
      );
    return (
      <ScrollArea className={styles.panel} data-testid="chat.mention.panel">
        {group.people.length + group.assistants.length === 0 ? (
          <div className={styles.empty}>{t('chat.mention.noAgents')}</div>
        ) : (
          <>
            {section(t('chat.group.peopleSection'), group.people, 'chat.mention.personRow')}
            {section(t('chat.group.assistantsSection'), group.assistants, 'chat.mention.assistantRow')}
          </>
        )}
      </ScrollArea>
    );
  }
  return (
    <ScrollArea className={styles.panel} data-testid="chat.mention.panel">
      <div className={styles.header}>{t('chat.mention.agentsSection')}</div>
      {agents.length === 0 ? (
        <div className={styles.empty}>{t('chat.mention.noAgents')}</div>
      ) : (
        agents.map((agent) => (
          <MentionRow key={agent.id} testId="chat.mention.agentRow" onPick={() => onPick(agent)}
            mark={<span aria-hidden className={styles.dot} style={{ background: colorForAgent(agent.id || agent.name) }} />} label={agent.name} />
        ))
      )}
    </ScrollArea>
  );
}

/** One row of the panel — an agent, a person or an assistant. */
function MentionRow({ testId, onPick, mark, label }: { testId: string; onPick: () => void; mark: ReactNode; label: string }) {
  return (
    <button
      type="button"
      className={styles.row}
      data-testid={testId}
      // Keep the textarea focused through the pick — a mousedown blur would
      // close the keyboard/caret before the click lands.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onPick}
    >
      {mark}
      <span className={styles.name}>{label}</span>
    </button>
  );
}
